import { useAuth } from './useAuth';
import type { Folder, Piece } from '@/types';
import { isDevMode } from '@/utils/devMode';

const BASE_URL = isDevMode() ? 'https://localhost:37743/v1' : 'https://api-workspace.pappers.fr/v1';
const NO_STATEMENT_DOCUMENTS_ERROR = 'No documents for the statement of facts';
const NO_STATEMENT_DOCUMENTS_MESSAGE = 'Aucune pièce disponible dans ce dossier. Ajoutez des pièces, puis réessayez.';

const extractErrorMessage = (details: unknown): string => {
	if (!details) {
		return '';
	}

	if (typeof details === 'string') {
		const trimmed = details.trim();
		if (!trimmed) {
			return '';
		}

		try {
			return extractErrorMessage(JSON.parse(trimmed));
		} catch {
			return trimmed;
		}
	}

	if (typeof details !== 'object') {
		return String(details);
	}

	const data = details as { error?: unknown; message?: unknown };
	if (data.error) {
		return extractErrorMessage(data.error);
	}
	if (data.message) {
		return extractErrorMessage(data.message);
	}

	return '';
};

export const formatStatementOfFactsError = (details?: unknown, status?: number): string => {
	const message = extractErrorMessage(details);

	if (message === NO_STATEMENT_DOCUMENTS_ERROR) {
		return NO_STATEMENT_DOCUMENTS_MESSAGE;
	}

	if (message) {
		return status ? `Erreur API ${status}: ${message}` : message;
	}

	return status ? `Erreur API: ${status}` : 'Erreur API';
};

const request = async <T>(path: string, options: RequestInit = {}): Promise<T> => {
	const { getToken } = useAuth();
	const authToken = await getToken();
	if (!authToken) {
		throw new Error('Non authentifié');
	}

	let response: Response;
	try {
		response = await fetch(`${BASE_URL}${path}`, {
			...options,
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${authToken}`,
				...options.headers
			}
		});
	} catch (e: any) {
		throw new Error(`Erreur réseau sur ${BASE_URL}${path}: ${e?.message || 'unknown error'}`);
	}

	if (!response.ok) {
		let details = '';
		try {
			details = await response.text();
		} catch {
			details = '';
		}
		const suffix = details ? ` - ${details}` : '';
		throw new Error(`Erreur API ${response.status} sur ${BASE_URL}${path}${suffix}`);
	}

	if (response.status === 204) {
		return undefined as T;
	}
	return response.json();
};

export const useWorkspaceApi = () => {
	const fetchFolders = async (organizationId: string): Promise<Folder[]> => {
		const data = await request<{ folders: Folder[] }>(`/organization/${organizationId}`);
		return data.folders;
	};

	const fetchPieces = async (folderId: string, query?: string, tag?: string): Promise<Piece[]> => {
		const searchParams = new URLSearchParams();
		if (query) searchParams.set('query', query);
		if (tag) searchParams.set('tags', tag);
		const params = searchParams.toString() ? `?${searchParams.toString()}` : '';
		const data = await request<{ documents: Piece[] }>(`/folder/${folderId}${params}`);
		return data.documents;
	};

	const fetchTags = async (folderId: string): Promise<string[]> => {
		const data = await request<{ tags: string[] }>(`/folder/${folderId}/tags`);
		return [...new Set(data.tags)];
	};

	const updateDocumentNumbers = async (folderId: string, updates: Record<string, number | null>): Promise<void> => {
		await request(`/folder/${folderId}/document/bulk/number`, {
			method: 'POST',
			body: JSON.stringify({ updates, mode: 'word' })
		});
	};

	const syncExportStatus = async (folderId: string, ids: string[], includeExport = true): Promise<void> => {
		if (!ids.length) return;
		await request(`/folder/${folderId}/document/bulk/export`, {
			method: 'POST',
			body: JSON.stringify({ ids, includeExport })
		});
	};

	const generateStatementOfFacts = async (folderId: string): Promise<string> => {
		const { getToken } = useAuth();
		const authToken = await getToken();
		if (!authToken) {
			throw new Error('Non authentifié');
		}

		const response = await fetch(`${BASE_URL}/folder/${folderId}/statement-of-facts`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Bearer ${authToken}`
			},
			body: JSON.stringify({})
		});

		if (!response.ok) {
			const details = await response.text().catch(() => '');
			throw new Error(formatStatementOfFactsError(details, response.status));
		}

		// Lecture du stream SSE
		const reader = response.body!.getReader();
		const decoder = new TextDecoder();
		let text = '';
		let pending = '';

		const appendDataLine = (line: string) => {
			const match = line.match(/^data:\s?(.*)$/);
			if (!match) return;

			const data = match[1];
			if (!data || data === '[DONE]') return;

			try {
				const parsed = JSON.parse(data);
				text += typeof parsed === 'string' ? parsed : String(parsed);
			} catch {
				text += data;
			}
		};

		const flushLines = (chunk: string, isFinal = false) => {
			pending += chunk;
			const lines = pending.split(/\r?\n/);
			pending = lines.pop() ?? '';

			for (const line of lines) {
				appendDataLine(line);
			}

			if (isFinal && pending) {
				appendDataLine(pending);
				pending = '';
			}
		};

		while (true) {
			const { done, value } = await reader.read();
			if (done) {
				break;
			}
			flushLines(decoder.decode(value, { stream: true }));
		}

		flushLines(decoder.decode(), true);

		return text;
	};

	return {
		fetchFolders,
		fetchPieces,
		fetchTags,
		updateDocumentNumbers,
		syncExportStatus,
		generateStatementOfFacts
	};
};
